import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, UserIcon, PhoneIcon } from '@heroicons/react/24/outline';
import FollowUpService from '../services/followUpService';
import EmployeeDashboardService from '../services/employeeDashboardService';
import api, { leadsApi } from '../config/api';
import StatusChangeModal from './StatusChangeModal';

const FollowUpModal = ({ 
  isOpen, 
  onClose, 
  lead, 
  onFollowUpAdded,
  currentUser = null 
}) => {
  const [formData, setFormData] = useState({
    type: 'call',
    summary: '',
    notes: '',
    nextFollowUpDate: '',
    nextFollowUpTime: '',
    nextStep: '',
    priority: 'medium',
    updateLeadStatus: false,
    newLeadStatus: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [availableTransitions, setAvailableTransitions] = useState([]);
  const [statusModal, setStatusModal] = useState({ isOpen: false, newStatus: '' });

  // Fetch lead statuses when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: 'call',
        summary: '',
        notes: '',
        nextFollowUpDate: '',
        nextFollowUpTime: '',
        nextStep: '',
        priority: 'medium',
        updateLeadStatus: false,
        newLeadStatus: ''
      });
      setError('');
      fetchLeadStatuses();
    }
  }, [isOpen]);

  // Update available transitions when lead status changes
  useEffect(() => {
    if (lead && leadStatuses.length > 0) {
      setAvailableTransitions(leadStatuses);
    }
  }, [lead, leadStatuses]);

  const fetchLeadStatuses = async () => {
    try {
      const response = await api.get('/lead-statuses');
      if (response.data.success) {
        setLeadStatuses(response.data.statuses);
      }
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    }
  };

  const handleStatusChange = async (remark) => {
    try {
      await leadsApi.put(`/leads/${lead._id}`, { 
        status: statusModal.newStatus, 
        statusRemark: remark || `Status updated to ${statusModal.newStatus}` 
      });
      onFollowUpAdded?.(); // Refresh lead
      setStatusModal({ isOpen: false, newStatus: '' });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update lead status: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.summary.trim()) {
      setError('Summary is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let combinedDate = formData.nextFollowUpDate;
      if (formData.nextFollowUpDate && formData.nextFollowUpTime) {
        const localDate = new Date(`${formData.nextFollowUpDate}T${formData.nextFollowUpTime}`);
        if (!isNaN(localDate.getTime())) {
          combinedDate = localDate.toISOString();
        } else {
          combinedDate = `${formData.nextFollowUpDate}T${formData.nextFollowUpTime}`;
        }
      } else if (formData.nextFollowUpDate) {
        const localDate = new Date(`${formData.nextFollowUpDate}T00:00:00`);
        if (!isNaN(localDate.getTime())) {
          combinedDate = localDate.toISOString();
        }
      }

      let resolvedUser = currentUser;
      if (!resolvedUser) {
        try {
          const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('userData');
          if (storedUser) resolvedUser = JSON.parse(storedUser);
        } catch (e) {
          console.error('Error parsing user info in FollowUpModal:', e);
        }
      }

      const followUpData = {
        ...formData,
        nextFollowUpDate: combinedDate,
        userId: resolvedUser?.id || resolvedUser?._id
      };

      const result = await FollowUpService.addFollowUp(lead._id, followUpData);

      if (result.success) {

        
        onFollowUpAdded?.(result.data);
        onClose();
      } else {
        setError(result.message || 'Failed to add follow-up');
      }
    } catch (error) {
      console.error('Error adding follow-up:', error);
      setError('Failed to add follow-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  const followUpTypes = FollowUpService.getFollowUpTypes();
  const priorityOptions = FollowUpService.getPriorityOptions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] mt-16 mb-4 overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Follow-up</h2>
            <p className="text-sm text-gray-600 mt-1">
              Lead: {lead?.name || 'Unknown'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative group">
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-sm transition-all text-sm font-medium"
              >
                Update Status
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[60] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Select New Status
                  </div>
                  {leadStatuses.map((s) => (
                    <button
                      key={s._id || s.name}
                      type="button"
                      onClick={() => setStatusModal({ isOpen: true, newStatus: s.name })}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-3 capitalize transition-colors"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${s.color ? `bg-${s.color}-500` : 'bg-blue-500'}`}></span>
                      {s.label || s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Lead Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Name:</span>
                <span className="ml-2 font-medium">{lead?.name || 'N/A'}</span>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Phone:</span>
                <span className="ml-2 font-medium">{lead?.phone || 'N/A'}</span>
              </div>
              <div className="flex items-center overflow-hidden">
                <span className="text-gray-600 shrink-0">Email:</span>
                <span className="ml-2 font-medium truncate" title={lead?.email || 'N/A'}>{lead?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Follow-up Type and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Follow-up Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {followUpTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {priorityOptions.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Summary *
            </label>
            <input
              type="text"
              name="summary"
              value={formData.summary}
              onChange={handleInputChange}
              placeholder="Brief summary of the follow-up..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="4"
              placeholder="Detailed notes about the follow-up..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Next Follow-up Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="nextFollowUpDate"
                    value={formData.nextFollowUpDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <CalendarIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <div className="relative">
                  <input
                    type="time"
                    name="nextFollowUpTime"
                    value={formData.nextFollowUpTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Step
              </label>
              <input
                type="text"
                name="nextStep"
                value={formData.nextStep}
                onChange={handleInputChange}
                placeholder="What's the next action to take?"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>



          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Follow-up'}
            </button>
          </div>
        </form>
      </div>

      <StatusChangeModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ isOpen: false, newStatus: '' })}
        lead={lead}
        newStatus={statusModal.newStatus}
        statusHistory={lead?.statusHistory || []}
        viewHistoryOnly={false}
        onConfirm={handleStatusChange}
      />
    </div>
  );
};

export default FollowUpModal;
